import dayjs from 'dayjs';
import {getCompanySubdomain} from "bukku/shared/ApiService";
import Big from "big.js";
import {select} from "redux-saga/effects";

const cs = getCompanySubdomain();

class LocalStorageHelper {
    storageKey = `store_${cs}`;

    getInitState() {
        localStorage.removeItem(this.storageKey);
        return {};
    }

    readStorage() {
        try {
            const jsonString = localStorage.getItem(this.storageKey);
            const state = jsonString ? JSON.parse(jsonString) : this.getInitState();

            if (!state.shoppingCart || !state.customerOrder || !state.totals || !state.shoppingCartSecret)
                return this.getInitState();

            if (state.lastUpdated) {
                const lastUpdated = dayjs(state.lastUpdated, "YYYY-MM-DD HH:mm:ss");
                const secDiff = dayjs().diff(lastUpdated, 'day');
                // If more than 3 days
                if (secDiff > 3)
                    return this.getInitState();
            }
            return {
                shoppingCart: state.shoppingCart,
                shoppingCartSecret: state.shoppingCartSecret,
                customerOrder: state.customerOrder,
                totals: state.totals,
            };
        }
        catch (e) {
            // Error in fetching local storage
            return this.getInitState();
        }
    }

    writeToStorage(state) {
        const value = {
            shoppingCart: state.shoppingCart,
            shoppingCartSecret: state.shoppingCartSecret,
            customerOrder: state.customerOrder,
            totals: state.totals,
            lastUpdated: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        const jsonString = JSON.stringify(value);
        localStorage.setItem(this.storageKey, jsonString);
        return state;
    }

    generateSecret = () => Math.random().toString(36);

    getProductIdsToRequest = function* (overwrite) {
        const { Store } = yield select();
        const { shoppingCart, productData } = Store;

        const productIdsToRequest = [];
        shoppingCart.forEach(item => {
            const { productId } = item;

            const price = productData.find(p => p.id === productId);
            // If not exists or overwrite
            if (!price || overwrite || !price.lastUpdated) {
                productIdsToRequest.push(productId);
            }
            // If exists or not overwrite
            else {
                const lastUpdated = dayjs(price.lastUpdated, "YYYY-MM-DD HH:mm:ss");
                const timeElapsed = dayjs().diff(lastUpdated, 'minute');

                // If more than 10 minutes
                if (timeElapsed > 10)
                    productIdsToRequest.push(productId);
            }
        });
        return productIdsToRequest;
    };

    updateProductData = (responseData, productData) => {
        const newProductData = [...productData];
        for (const data of responseData) {
            const newProduct = {
                id: data.id,
                isAvailable: data.is_available,
                name: data.name,
                pictureUrl: data.picture_url,
                thumbnailUrl: data.thumbnail_url,
                sku: data.sku,
                unitLabel: data.unit_label,
                unitPrice: data.unit_price,
                lastUpdated: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            };

            const idx = productData.findIndex(p => p.id === data.id);
            // If not found, add to <productData>
            if (idx < 0)
                newProductData.push(newProduct);

            else
                newProductData[idx] = newProduct;
        }
        return newProductData;
    };

    /**
     * Get the correct Price Level item based on current qty
     * assumption: quantity > 0.
     * @param { Array [{min_qty:int, unit_price: float}..] } salePrices -- can be an unordered array
     * @param {float} quantity -- quantity of item in cart
     */
    getItemPriceLevelAtQuantity = (salePrices, quantity) => {
        if (salePrices && salePrices.length) {
            if (salePrices.length === 1)
                return salePrices[0]; // skip comparison

            return salePrices.reduce((acc, item) => (
                item.min_qty <= quantity &&
                item.min_qty >= acc.min_qty ?
                    item :
                    acc
            ), salePrices[0],
            );
        }
        return null;
    };

    calculateAmounts(shoppingCart, productData) {
        let grandTotal = new Big(0);
        const productsWithPriceChanges = [];
        const newShoppingCart = shoppingCart.map(item => {
            const newItem = {...item};
            const product = productData.find(p => p.id === item.productId);
            if (product) {
                const priceLevel = this.getItemPriceLevelAtQuantity(product.unitPrice, item.qty);
                const unitPriceAtQty = priceLevel.unit_price;
                const amount = new Big(item.qty || 0).times(unitPriceAtQty || 0).round(2);
                newItem.name = product.name;
                newItem.priceLevels = product.unitPrice; // list of price levels
                newItem.priceLevel = priceLevel; // current price level (based on qty)
                newItem.unitPrice = unitPriceAtQty;
                newItem.amount = parseFloat(amount);
                newItem.unitLabel = product.unitLabel;
                grandTotal = grandTotal.plus(amount);

                if (newItem.unitPrice !== item.unitPrice &&
                     newItem.unitPrice !== undefined &&
                     item.unitPrice !== undefined) {
                    productsWithPriceChanges.push({
                        productId: newItem.productId,
                        name: newItem.name,
                        oldPrice: item.unitPrice,
                        oldUnitLabel: item.unitLabel,
                        newPrice: newItem.unitPrice,
                        newUnitLabel: newItem.unitLabel,
                    });
                }
            }
            return newItem;
        });

        const newTotals = {
            grandTotal: parseFloat(grandTotal),
        };

        return [newShoppingCart, newTotals, productsWithPriceChanges];
    }

    checkProductNotAvailable = (requestProductIds, response, shoppingCart) => {
        const productsNotAvailable = [];
        const productsOutOfStock = [];
        const toRemove = [];

        requestProductIds.forEach(id => {
            const item = shoppingCart.find(item => item.productId === id);
            const existInShoppingCart = !!item;

            if (existInShoppingCart) {
                const product = response.find(product => product.id === id);
                const notAvailable = !product;

                const outOfStock = !!product && !product?.is_available;

                if (notAvailable) {
                    productsNotAvailable.push({productId: id, name: item.name});
                    toRemove.push(id);
                }
                else if (outOfStock) {
                    productsOutOfStock.push({productId: id, name: item.name});
                    toRemove.push(id);
                }
            }
        });

        // Update shoppingCart by removing productsNotAvailable and productsOutOfStock
        const newShoppingCart = shoppingCart.filter(item => !toRemove.find(id => id === item.productId));
        return [newShoppingCart, productsNotAvailable, productsOutOfStock];
    };
}

export const Helper = new LocalStorageHelper();
